import * as React from 'react';
import { cn } from '../../lib/utils';

/* ────────────────────────────────────────────────────────────────────────────
   v9 semantic card variants. All are rounded-2xl with a 1px border.
   They render as plain divs so they can wrap arbitrary children without
   the heading/title structure that shadcn's Card imposes. Use shadcn Card
   directly when you actually want CardHeader/CardTitle/CardContent.
   ──────────────────────────────────────────────────────────────────────────── */

const variants = {
  /* Default white card. .h-card */
  default: 'bg-white border border-slate-200',
  /* Muted info card (grey). .h-card-info-grey */
  muted: 'bg-slate-50 border border-slate-200',
  /* Next-step / blue handoff card. .h-card-next */
  next: 'bg-blue-50 border border-blue-200',
  /* Warn / yellow card. .h-card-warn */
  warn: 'bg-amber-50 border border-amber-200',
  /* Good / green confirmation. .h-card-good */
  good: 'bg-green-50 border border-green-200',
};

export const HCard = React.forwardRef(function HCard(
  { variant = 'default', className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('rounded-2xl', variants[variant] || variants.default, className)}
      {...rest}
    >
      {children}
    </div>
  );
});

/* Tap-target version of HCard. Renders as <button> with subtle hover.
   Used for cards that navigate or open something on tap (e.g. Hearth row,
   Higlo row in the list). */
export const HCardButton = React.forwardRef(function HCardButton(
  { variant = 'default', className, children, type = 'button', disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      className={cn(
        'block w-full text-left rounded-2xl transition-all',
        variants[variant] || variants.default,
        !disabled && 'hover:bg-slate-50 hover:border-slate-300 hover:shadow-[0_1px_3px_rgba(15,23,42,0.06)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2',
        disabled && 'opacity-60 cursor-not-allowed',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
